import { faLayerGroup, faPlug } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate } from '@tanstack/react-router'
import { observer } from 'mobx-react-lite'
import { useCallback, useContext, useRef } from 'react'
import type { ClientConnectionConfig, ConnectionCollection } from '@companion-app/shared/Model/Connections.js'
import { ModuleInstanceType } from '@companion-app/shared/Model/Instance.js'
import type { InstanceStatusEntry } from '@companion-app/shared/Model/InstanceStatus.js'
import { stringifyError } from '@companion-app/shared/Stringify.js'
import { Button, ButtonGroup } from '~/Components/Button.js'
import { CollectionsNestingTable } from '~/Components/CollectionsNestingTable/CollectionsNestingTable.js'
import { GenericConfirmModal, type GenericConfirmModalRef } from '~/Components/GenericConfirmModal.js'
import { NonIdealState } from '~/Components/NonIdealState.js'
import { SwitchInputField } from '~/Components/SwitchInputField.js'
import { useTableVisibilityHelper, VisibilityButton } from '~/Components/TableVisibility.js'
import { PanelCollapseHelperProvider } from '~/Helpers/CollapseHelper.js'
import { ContextHelpButton } from '~/Layout/PanelIcons.js'
import { MyErrorBoundary } from '~/Resources/Error.js'
import { trpc, useMutationExt } from '~/Resources/TRPC.js'
import { useComputed } from '~/Resources/util.js'
import { RootAppStoreContext } from '~/Stores/RootAppStore.js'
import { MissingVersionsWarning } from '../../Instances/MissingVersionsWarning.js'
import { ConnectionVariablesModal, type ConnectionVariablesModalRef } from '../ConnectionVariablesModal.js'
import { useConnectionCollectionsApi } from './ConnectionListApi.js'
import { ConnectionListContextProvider, useConnectionListContext } from './ConnectionListContext.js'
import { ConnectionsTableRow } from './ConnectionsTableRow.js'

export interface VisibleConnectionsState {
	disabled: boolean
	ok: boolean
	warning: boolean
	error: boolean
}

interface ConnectionsListProps {
	selectedConnectionId: string | null
}

export const ConnectionsList = observer(function ConnectionsList({ selectedConnectionId }: ConnectionsListProps) {
	const { connections, instanceStatuses } = useContext(RootAppStoreContext)

	const navigate = useNavigate({ from: '/connections' })
	const doConfigureConnection = useCallback(
		(connectionId: string | null) => {
			if (!connectionId) {
				void navigate({ to: '/connections' })
			} else {
				void navigate({ to: `/connections/$connectionId`, params: { connectionId } })
			}
		},
		[navigate]
	)

	const confirmModalRef = useRef<GenericConfirmModalRef>(null)
	const variablesModalRef = useRef<ConnectionVariablesModalRef>(null)

	const showConnectionVariables = useCallback(
		(connectionId: string) => variablesModalRef.current?.show(connectionId),
		[]
	)

	const visibleConnections = useTableVisibilityHelper<VisibleConnectionsState>('connections_visible', {
		disabled: true,
		ok: true,
		warning: true,
		error: true,
	})

	const connectionListApi = useConnectionCollectionsApi(confirmModalRef)

	const allConnections = useComputed(() => {
		const allConnections: ClientConnectionConfigWithId[] = []

		for (const [connectionId, connection] of connections.connections) {
			const status = instanceStatuses.getStatus(connectionId)
			allConnections.push({ ...connection, id: connectionId, status })
		}

		return allConnections
	}, [connections.connections, instanceStatuses])

	const ConnectionsItemRow = useCallback(
		(item: ClientConnectionConfigWithId) =>
			ConnectionListItemWrapper(visibleConnections.visibility, item, selectedConnectionId),
		[visibleConnections.visibility, selectedConnectionId]
	)

	return (
		<div className="connections-list-container flex-column-layout">
			<div className="connections-list-header fixed-header">
				<h4 className="button-inline">
					Connections <ContextHelpButton action="/user-guide/config/connections" />
				</h4>

				<p>
					When you want to control devices or software with Companion, you need to add a connection to let Companion
					know how to communicate with whatever you want to control.
				</p>

				<MissingVersionsWarning moduleType={ModuleInstanceType.Connection} instances={connections.connections} />

				<GenericConfirmModal ref={confirmModalRef} />
				<ConnectionVariablesModal ref={variablesModalRef} />

				<ButtonGroup className="connection-group-actions mb-2">
					<Button
						color="primary"
						size="sm"
						className="d-xl-none"
						onClick={() => void navigate({ to: '/connections/add' })}
					>
						<FontAwesomeIcon icon={faPlug} className="me-1" />
						Add Connection
					</Button>
					<CreateCollectionButton />
				</ButtonGroup>
			</div>

			<div className="connections-list-table-container scrollable-content">
				<PanelCollapseHelperProvider
					storageId="connection-collections"
					knownPanelIds={connections.allCollectionIds}
					defaultCollapsed
				>
					<ConnectionListContextProvider
						visibleConnections={visibleConnections}
						showVariables={showConnectionVariables}
						deleteModalRef={confirmModalRef}
						configureConnection={doConfigureConnection}
					>
						<CollectionsNestingTable<ConnectionCollection, ClientConnectionConfigWithId>
							Heading={ConnectionListTableHeading}
							NoContent={ConnectionListNoConnections}
							ItemRow={ConnectionsItemRow}
							GroupHeaderContent={ConnectionGroupHeaderContent}
							itemName="connection"
							dragId="connection"
							collectionsApi={connectionListApi}
							collections={connections.rootCollections()}
							items={allConnections}
							selectedItemId={selectedConnectionId}
						/>
					</ConnectionListContextProvider>
				</PanelCollapseHelperProvider>
			</div>
		</div>
	)
})

export interface ClientConnectionConfigWithId extends ClientConnectionConfig {
	id: string
	status: InstanceStatusEntry | undefined
}

function ConnectionListTableHeading() {
	const { visibleConnections } = useConnectionListContext()

	return (
		<div className="flex flex-row">
			<div className="grow">Connection</div>
			<div className="no-break">
				<ButtonGroup className="table-header-buttons">
					<VisibilityButton {...visibleConnections} keyId="disabled" color="secondary" label="Disabled" />
					<VisibilityButton {...visibleConnections} keyId="ok" color="success" label="OK" />
					<VisibilityButton {...visibleConnections} keyId="warning" color="warning" label="Warning" />
					<VisibilityButton {...visibleConnections} keyId="error" color="danger" label="Error" />
				</ButtonGroup>
			</div>
		</div>
	)
}

function ConnectionListNoConnections() {
	return (
		<NonIdealState icon={faPlug}>
			You haven't set up any connections yet. <br />
			Try adding something from the list <span className="d-xl-none">below</span>
			<span className="d-none d-xl-inline">to the right</span>.
		</NonIdealState>
	)
}

function ConnectionGroupHeaderContent({ collection }: { collection: ConnectionCollection }) {
	const setEnabledMutation = useMutationExt(trpc.instances.connections.collections.setEnabled.mutationOptions())

	const setEnabled = useCallback(
		(enabled: boolean) => {
			setEnabledMutation.mutateAsync({ collectionId: collection.id, enabled }).catch((e) => {
				console.error('Failed to set collection enabled state', stringifyError(e))
			})
		},
		[setEnabledMutation, collection.id]
	)

	return (
		<div className="ms-1">
			<SwitchInputField
				id={undefined}
				value={collection.metaData.enabled}
				setValue={setEnabled}
				tooltip={collection.metaData.enabled ? 'Disable collection' : 'Enable collection'}
			/>
		</div>
	)
}

function ConnectionListItemWrapper(
	visibility: VisibleConnectionsState,
	item: ClientConnectionConfigWithId,
	selectedItemId: string | null
) {
	// Apply visibility filters
	if (!visibility.disabled && item.enabled === false) {
		return null
	} else if (item.status) {
		if (!visibility.ok && item.status.category === 'good') {
			return null
		} else if (!visibility.warning && item.status.category === 'warning') {
			return null
		} else if (!visibility.error && item.status.category === 'error') {
			return null
		}
	}

	return (
		<MyErrorBoundary>
			<ConnectionsTableRow connection={item} isSelected={selectedItemId === item.id} />
		</MyErrorBoundary>
	)
}

function CreateCollectionButton() {
	const createMutation = useMutationExt(trpc.instances.connections.collections.add.mutationOptions())

	const doCreateCollection = useCallback(() => {
		createMutation.mutateAsync({ collectionName: 'New Collection' }).catch((e) => {
			console.error('Failed to add collection', e)
		})
	}, [createMutation])

	return (
		<Button color="info" size="sm" onClick={doCreateCollection}>
			<FontAwesomeIcon icon={faLayerGroup} /> Create Collection
		</Button>
	)
}
